import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertCircle, ChevronRight, CheckCircle2, UserPlus, Clock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

const POLL_MS = 60000;

const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function NotificationBell() {
  const [data, setData] = useState({ count: 0, items: [], mois: "", type: "unpaid" });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setData(res.data || { count: 0, items: [], mois: "", type: "unpaid" });
    } catch {
      // silent fail — bell stays at last known value
    }
  }, []);

  useEffect(() => {
    fetchNotifs();
    const id = setInterval(fetchNotifs, POLL_MS);
    return () => clearInterval(id);
  }, [fetchNotifs]);

  const goToPaiement = (item) => {
    setOpen(false);
    navigate(`/paiements?locataire=${item.locataire_id}`);
  };
  const goToDevSignups = () => {
    setOpen(false);
    navigate("/dev");
  };

  const total = data.count || 0;
  const hasUnread = total > 0;
  const isDev = data.type === "dev";

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) fetchNotifs(); }}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
          data-testid="topbar-notifs"
          title={hasUnread ? (isDev ? `${total} inscription(s) en attente` : `${total} impayé(s) ce mois`) : "Aucune notification"}
        >
          <Bell className="w-5 h-5 text-teal-100/90" strokeWidth={1.75} />
          {hasUnread && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[#1F5F5B]"
              data-testid="notif-badge"
            >
              {total > 9 ? "9+" : total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[calc(100vw-1.5rem)] sm:w-[380px] max-w-[380px] p-0 border border-slate-200 shadow-2xl"
        data-testid="notif-popover"
      >
        <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-[#1F5F5B]/5 to-orange-500/5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-orange-600 font-bold">
                {isDev ? "Espace DEV" : "Échéancier"}
              </div>
              <div className="text-sm font-semibold text-slate-800">
                {isDev ? "Inscriptions en attente" : `Impayés — ${data.mois || "—"}`}
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200">
              {isDev ? <UserPlus className="w-3.5 h-3.5 text-orange-500" /> : <AlertCircle className="w-3.5 h-3.5 text-orange-500" />}
              <span className="text-xs font-semibold text-slate-700" data-testid="notif-count-label">
                {isDev ? `${total} demande${total > 1 ? "s" : ""}` : `${total} impayé${total > 1 ? "s" : ""}`}
              </span>
            </div>
          </div>
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="px-6 py-10 flex flex-col items-center justify-center gap-2 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              </div>
              <div className="text-sm font-medium text-slate-700">
                {isDev ? "Aucune demande en attente" : "Tous les loyers sont à jour"}
              </div>
              <div className="text-xs text-slate-500">
                {isDev ? "Les nouvelles demandes d'inscription apparaîtront ici." : "Aucun impayé pour le mois en cours."}
              </div>
            </div>
          ) : isDev ? (
            <ul className="divide-y divide-slate-100" data-testid="notif-dev-list">
              {data.items.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={goToDevSignups}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-start gap-3 group"
                    data-testid={`notif-signup-${item.id}`}
                  >
                    <div className="mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-orange-50 text-orange-600">
                      <UserPlus className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{item.society_nom}</div>
                      <div className="text-xs text-slate-500 truncate">{item.email}</div>
                      <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-400">
                        <Clock className="w-3 h-3" /> {fmtDate(item.created_at)}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-orange-500 transition-colors mt-2" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.items.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => goToPaiement(item)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-start gap-3 group"
                    data-testid={`notif-item-${item.locataire_id}`}
                  >
                    <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      item.type === "impaye"
                        ? "bg-rose-50 text-rose-600"
                        : item.type === "temp_expiring"
                          ? "bg-orange-50 text-orange-600"
                          : "bg-amber-50 text-amber-600"
                    }`}>
                      <AlertCircle className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-semibold text-slate-800 truncate">
                          {item.locataire_nom}
                        </div>
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                          item.type === "impaye"
                            ? "bg-rose-100 text-rose-700"
                            : item.type === "temp_expiring"
                              ? "bg-orange-100 text-orange-700"
                              : "bg-amber-100 text-amber-700"
                        }`}>
                          {item.type === "impaye" ? "Impayé" : item.type === "temp_expiring" ? (item.expires_today ? "Expire aujourd'hui" : "Expire demain") : "Partiel"}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {item.maison_nom}{item.plan_id ? ` · ${item.plan_id}` : ""}
                      </div>
                      {item.type === "temp_expiring" ? (
                        <div className="mt-1.5 text-xs text-orange-700 font-medium">
                          Fin de séjour : {new Date(item.sejour_end_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "long" })}
                        </div>
                      ) : (
                        <div className="mt-1.5 flex items-baseline gap-2">
                          <span className="text-sm font-bold text-rose-600">
                            {fmt(item.solde)} FCFA
                          </span>
                          <span className="text-[11px] text-slate-400">
                            / {fmt(item.attendu)} dû
                          </span>
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-orange-500 transition-colors mt-2" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {!isDev && data.items.length > 0 && (
          <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/60">
            <button
              onClick={() => { setOpen(false); navigate("/rapports"); }}
              className="text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors"
              data-testid="notif-open-rapports"
            >
              Voir tous les loyers attendus →
            </button>
          </div>
        )}
        {isDev && data.items.length > 0 && (
          <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/60">
            <button
              onClick={goToDevSignups}
              className="text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors"
              data-testid="notif-open-dev"
            >
              Gérer les demandes dans l'espace DEV →
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
